/**
 * Shared visual language for mindmap-node pills.
 *
 * Three renderers draw the same pill and must agree: the DOM overlay
 * (`excalidraw-app/.../RenderMindmapNode.tsx`), the canvas export
 * (`renderer/renderElement.ts`) and the SVG export
 * (`renderer/staticSvgScene.ts`). Keeping the geometry and colours here means a
 * restyle touches one file instead of three. It lives in `packages/excalidraw`
 * because the two export renderers do, and `packages/excalidraw` must never
 * import from `excalidraw-app`.
 */

/**
 * Horizontal gap an expandable node reserves on its trailing edge for the
 * expand/collapse chip. The single definition — `excalidraw-app`'s layout engine
 * re-exports it as `CHIP_ALLOWANCE` rather than keeping its own copy, which used
 * to drift the exports away from the on-canvas pill.
 */
export const MINDMAP_CHIP_ALLOWANCE = 28;

/** Canvas tone the pill fill is mixed toward in dark theme. */
const DARK_CANVAS_TONE = { r: 0x1e, g: 0x1e, b: 0x1e };

const WHITE = { r: 255, g: 255, b: 255 };

const parseHex = (hex: string): { r: number; g: number; b: number } | null => {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) {
    return null;
  }
  const int = parseInt(match[1], 16);
  return { r: (int >> 16) & 0xff, g: (int >> 8) & 0xff, b: int & 0xff };
};

const mixToward = (
  hex: string,
  target: { r: number; g: number; b: number },
  amount: number,
): string => {
  const rgb = parseHex(hex);
  if (!rgb) {
    return hex;
  }
  const mix = (c: number, t: number) => Math.round(c + (t - c) * amount);
  return `rgb(${mix(rgb.r, target.r)}, ${mix(rgb.g, target.g)}, ${mix(
    rgb.b,
    target.b,
  )})`;
};

/** Blend a hex colour toward white. `amount` is the fraction of white. */
const lighten = (hex: string, amount: number): string =>
  mixToward(hex, WHITE, amount);

/** Blend a hex colour toward the dark canvas tone. */
const darken = (hex: string, amount: number): string =>
  mixToward(hex, DARK_CANVAS_TONE, amount);

/**
 * The root pill is the one heavy plate on the canvas — a saturated navy with
 * light text, matching the reference maps' anchor node. Deliberately fixed in
 * both themes: it is an identity marker, not a tint of the branch palette (the
 * root's branch colour is the neutral black every uncoloured spine node uses,
 * which would make it indistinguishable).
 */
export const MINDMAP_ROOT_FILL = "#243b6b";
const MINDMAP_ROOT_TEXT = "#f5f7fb";

/**
 * Pill background. Light theme is a near-white tint of the branch colour; dark
 * theme mixes the same colour toward the canvas instead, because the light tint
 * plus the dark theme's white text rendered white-on-white. The root ignores
 * both and takes the fixed anchor plate.
 */
export const getMindmapPillFill = (
  branchColor: string,
  isDark: boolean,
  isRoot = false,
): string =>
  isRoot
    ? MINDMAP_ROOT_FILL
    : isDark
    ? darken(branchColor, 0.78)
    : lighten(branchColor, 0.85);

/** Label colour that stays legible on `getMindmapPillFill`. */
export const getMindmapPillTextColor = (
  isDark: boolean,
  isRoot = false,
): string => (isRoot ? MINDMAP_ROOT_TEXT : isDark ? "#e8e8e8" : "black");

/** Pill border colour: the branch colour, or the root's own darker plate. */
export const getMindmapPillBorderColor = (
  branchColor: string,
  isRoot = false,
): string => (isRoot ? MINDMAP_ROOT_FILL : branchColor);

/** Border thickness every renderer draws the pill outline at. */
export const MINDMAP_PILL_BORDER_WIDTH = 1.5;

/**
 * Horizontal width the border steals from the label. The DOM pill is
 * `box-sizing: border-box`, so its body — and therefore the label — gets the
 * element width MINUS both borders; the estimator used to charge nothing for
 * them and hand the renderer a label box 3px narrower than it had promised.
 */
export const MINDMAP_PILL_BORDER_CHROME = 2 * MINDMAP_PILL_BORDER_WIDTH;

/**
 * Whether a mindmap node element is its map's root — the node whose blockId is
 * the map's `rootPageId`. Lives here because all three pill renderers need the
 * same answer and none of them may reach into `excalidraw-app`.
 */
export const isMindmapRootPill = (element: {
  customData?: Record<string, any> | null;
}): boolean => {
  const mindmap = element.customData?.mindmap;
  return (
    !!mindmap &&
    typeof mindmap.blockId === "string" &&
    mindmap.blockId === mindmap.rootPageId
  );
};

/** Font stack the exports use for the pill label, matching the DOM UI font. */
export const MINDMAP_PILL_FONT_FAMILY =
  "Assistant, system-ui, -apple-system, sans-serif";

export const MINDMAP_PILL_FONT_SIZE = 14;

/** Horizontal inset the label is clipped to, matching the DOM pill's padding. */
export const MINDMAP_PILL_PADDING = 16;

/** Narrowest pill we will draw. Resizing a node below the chip allowance would
 * otherwise yield a negative width and an invalid SVG viewBox (S4). */
export const MIN_MINDMAP_PILL_WIDTH = 24;

/**
 * Advance width of an ordinary label glyph at `MINDMAP_PILL_FONT_SIZE`. The pill
 * is measured arithmetically rather than with a real text metric because the
 * layout engine runs before any DOM exists; the renderer's `line-clamp` is the
 * authority on where the text actually breaks, and this only has to agree with
 * it closely enough that the reserved box fits.
 */
export const MINDMAP_PILL_CHAR_WIDTH = 8;

/**
 * Advance widths for the glyph classes that are nowhere near the average.
 *
 * One flat width per character under-measured any label carrying capitals or
 * `m`/`w` — "Dummy" needs ~49px and was charged 40, so the DOM pill wrapped a
 * five-letter word mid-word ("Dum / my") inside a box the estimator swore was
 * wide enough. Three buckets is still coarse, but it puts the error on the safe
 * side of the words that actually appear in Notion titles.
 */
const MINDMAP_PILL_NARROW_CHAR_WIDTH = 4.5;
const MINDMAP_PILL_WIDE_CHAR_WIDTH = 12;
const MINDMAP_PILL_CAP_CHAR_WIDTH = 9;

const NARROW_GLYPHS = new Set("ijlrtfI.,:;'\"!|()[]{}".split(""));
const WIDE_GLYPHS = new Set(["m", "w", "M", "W"]);

/** Estimated rendered width of `text` at the pill's font size. */
export const mindmapTextWidth = (text: string): number => {
  let width = 0;
  for (const char of text) {
    width += WIDE_GLYPHS.has(char)
      ? MINDMAP_PILL_WIDE_CHAR_WIDTH
      : NARROW_GLYPHS.has(char)
      ? MINDMAP_PILL_NARROW_CHAR_WIDTH
      : char >= "A" && char <= "Z"
      ? MINDMAP_PILL_CAP_CHAR_WIDTH
      : MINDMAP_PILL_CHAR_WIDTH;
  }
  return width;
};

/** Widest pill we will lay out; longer labels wrap, then ellipsize. */
export const MINDMAP_PILL_MAX_WIDTH = 320;

/** Lines a pill label may occupy before it ellipsizes. */
export const MINDMAP_PILL_MAX_LINES = 2;

/** Line box height of one label line (14px font at ~1.4 leading). */
export const MINDMAP_PILL_LINE_HEIGHT = 20;

/**
 * Vertical chrome above and below the label lines. Chosen so a one-line pill is
 * exactly 48px tall — the height every map laid out before wrapping existed, so
 * single-line maps keep their geometry and their tests.
 */
export const MINDMAP_PILL_VERTICAL_PADDING = 28;

/**
 * Lines the DOM pill may show inside a box `height` tall.
 *
 * The renderer hard-coded `MINDMAP_PILL_MAX_LINES`, so a label needing more than
 * two lines ellipsized no matter how tall its pill was: dragging a pill TALLER
 * revealed nothing, and only dragging it WIDER — more characters per line —
 * brought the rest of the text back (the owner's "why only width shows remaining
 * text"). Deriving the clamp from the box makes height work too; the `max` floor
 * keeps every auto-laid-out pill exactly as it renders today.
 */
export const mindmapPillClampLines = (height: number): number =>
  Math.max(
    MINDMAP_PILL_MAX_LINES,
    Math.floor(
      (height - MINDMAP_PILL_VERTICAL_PADDING) / MINDMAP_PILL_LINE_HEIGHT,
    ),
  );

/** Edge length of the square boxes the DOM pill gives the Notion glyph and the
 * node's own icon, and the flex `gap` between every pill child. */
export const MINDMAP_PILL_GLYPH_SIZE = 18;
export const MINDMAP_PILL_GLYPH_GAP = 8;

/** Width one leading glyph costs the label: its box plus the gap after it. */
export const MINDMAP_PILL_GLYPH_ALLOWANCE =
  MINDMAP_PILL_GLYPH_SIZE + MINDMAP_PILL_GLYPH_GAP;

export interface MindmapLabelBoxInput {
  /** The element's full box width, chip allowance included. */
  elementWidth: number;
  expandable: boolean;
  isPage: boolean;
  hasIcon: boolean;
}

/**
 * Width the label actually gets to wrap in, for an element of `elementWidth`.
 *
 * The ONE definition of the pill's horizontal chrome, and the reason it exists:
 * the layout engine used to compute the reserved height from its own private
 * chrome sum while the DOM pill wrapped at whatever its flex row left over. The
 * two disagreed by a glyph box plus a gap whenever a node carried an `icon`, so
 * a label the estimator sized for one line wrapped to two in the DOM and got
 * clamped away. Every path that asks "how wide is the text?" — the estimator's
 * line count, the PNG/SVG exports, the DOM pill — derives it from here.
 */
export const mindmapLabelBoxWidth = ({
  elementWidth,
  expandable,
  isPage,
  hasIcon,
}: MindmapLabelBoxInput): number => {
  const pillWidth = Math.max(
    MIN_MINDMAP_PILL_WIDTH,
    elementWidth - (expandable ? MINDMAP_CHIP_ALLOWANCE : 0),
  );
  const chrome =
    MINDMAP_PILL_BORDER_CHROME +
    MINDMAP_PILL_PADDING * 2 +
    (isPage ? MINDMAP_PILL_GLYPH_ALLOWANCE : 0) +
    (hasIcon ? MINDMAP_PILL_GLYPH_ALLOWANCE : 0);
  return Math.max(0, pillWidth - chrome);
};

/** How many lines `text` needs inside a label box `contentWidth` wide. */
export const mindmapPillLineCount = (
  text: string,
  contentWidth: number,
): number => wrapLabel(text, contentWidth, MINDMAP_PILL_MAX_LINES).lines.length;

/** Pill height for a label occupying `lines` lines. */
export const mindmapPillHeight = (lines: number): number =>
  MINDMAP_PILL_VERTICAL_PADDING + Math.max(1, lines) * MINDMAP_PILL_LINE_HEIGHT;

/** Longest prefix of `text` that fits `width`; at least one character. */
const prefixThatFits = (text: string, width: number): number => {
  let fitted = 0;
  let used = 0;
  for (const char of text) {
    used += mindmapTextWidth(char);
    if (used > width && fitted > 0) {
      break;
    }
    fitted += char.length;
    if (used > width) {
      break;
    }
  }
  return Math.max(1, fitted);
};

/**
 * Greedy word wrap of `text` into at most `maxLines` lines of `contentWidth`,
 * plus whatever did not fit. Breaks between words, and only ever mid-word for a
 * single token with no break opportunity at all (a URL, an id) — a pill wide
 * enough for its longest word therefore never splits one.
 */
const wrapLabel = (
  text: string,
  contentWidth: number,
  maxLines: number,
): { lines: string[]; rest: string } => {
  const width = Math.max(0, contentWidth);
  const lines: string[] = [];
  let rest = text.trim();

  while (rest.length > 0 && lines.length < Math.max(1, maxLines)) {
    if (mindmapTextWidth(rest) <= width) {
      lines.push(rest);
      rest = "";
      break;
    }
    const fits = prefixThatFits(rest, width);
    const space = rest.slice(0, fits + 1).lastIndexOf(" ");
    const cut = space > 0 ? space : fits;
    lines.push(rest.slice(0, cut).trimEnd());
    rest = rest.slice(cut).trimStart();
  }

  return { lines: lines.length > 0 ? lines : [""], rest };
};

/**
 * Split a label into the lines the exports draw: word-wrapped to `contentWidth`,
 * capped at `maxLines` with an ellipsis on the last one. The DOM pill gets this
 * for free from `-webkit-line-clamp`; the PNG and SVG exports have no layout
 * engine, so they wrap through here instead of clipping a single line. Callers
 * that know the pill's height pass `mindmapPillClampLines(height)` so a
 * user-heightened pill exports the same extra lines it shows on screen.
 */
export const mindmapPillLabelLines = (
  text: string,
  contentWidth: number,
  maxLines: number = MINDMAP_PILL_MAX_LINES,
): string[] => {
  const { lines, rest } = wrapLabel(text, contentWidth, maxLines);
  if (rest.length === 0) {
    return lines;
  }
  // Trim the truncated line back until the ellipsis itself fits.
  let last = lines[lines.length - 1];
  const room = Math.max(0, contentWidth - mindmapTextWidth("…"));
  while (last.length > 0 && mindmapTextWidth(last) > room) {
    last = last.slice(0, -1);
  }
  lines[lines.length - 1] = `${last.trimEnd()}…`;
  return lines;
};
